import type OpenAI from 'openai'

import { AI_CHATBOT_CONFIG } from './config'
import {
  calcCostUsd,
  calcSessionActualTokens,
  calcSessionCostUsd,
  createSessionCosts,
  creditsToTokens,
  recordTurnCost,
} from './cost-tracker'
import { getHostRuntime } from './host-runtime'
import { createLogger, type Logger } from './logger'
import {
  buildInitialMessages,
  getLastAssistantText,
  isPerTurnInjection,
  maybeConfirmedEntitiesMessage,
  maybePageContextMessage,
} from './message-history'
import { compactHistory } from './pipeline/compaction'
import { resolvePageContext, runGuard } from './pipeline/pipeline'
import { normalizeTools } from './runtime/compat'
import { withCache } from './runtime/tool-cache'
import {
  extractPartialMessage,
  isCompleteArguments,
  runToolDirect,
  type FunctionToolCall,
  type PartialAssistantMessage,
} from './runtime/tool-executor'
import {
  setConfirmedEntityWriter,
  setPerTurnCallbacks,
} from './session-context'
import { estimateMessagesTokens } from './token-estimator'
import { ToolPauseState } from './tool-pause'
import {
  DEFAULT_MODEL,
  type AiChatBotDebugLevel,
  type AiChatbotConfig,
  type ConfirmedEntity,
  type ConversationState,
  type SessionCosts,
  type StoredMessage,
  type ToolResult,
} from './types'

export type ConversationListener = (state: ConversationState) => void

// A tool result is an error when its JSON content carries an `error` field — the
// shape both thrown-tool failures and tools returning `{ error }` produce (see
// tool-executor.ts). Plain-string results are never errors.
function isToolErrorResult(content: string): boolean {
  try {
    const parsed: unknown = JSON.parse(content)
    return typeof parsed === 'object' && parsed !== null && 'error' in parsed
  } catch {
    return false
  }
}

// Tools whose accompanying preamble is worth showing: their result is a UI card
// the lead-in text introduces (e.g. "I found these companies:" above the
// OptionsCard). Every OTHER tool is an internal fetch (schema, search, data load)
// or a self-explanatory confirmation card, so preamble like "Let me get the write
// schema first" / "I'll create the ticket" is pure narration of an internal step.
// The model emits it regardless of prompt instructions, so it is suppressed here
// rather than via instructions it does not reliably follow.
const NARRATION_TOOLS = new Set(['show_options', 'show_filter_preview'])

function keepsPreamble(
  toolCalls:
    | ReadonlyArray<OpenAI.Chat.Completions.ChatCompletionMessageToolCall>
    | undefined,
): boolean {
  return (toolCalls ?? []).some(
    (tc) => tc.type === 'function' && NARRATION_TOOLS.has(tc.function.name),
  )
}

export class Conversation {
  private messages: OpenAI.ChatCompletionMessageParam[] = []
  private listener: ConversationListener
  private client: OpenAI
  private isProcessing = false
  private isThinking = false
  private hadError = false
  private abortController: AbortController | null = null
  private log: Logger
  private compactionDisabled = false
  private consecutiveCompactFailures = 0
  // Wrapped tools are cached per Conversation instance so ExpiryMap state survives across turns.
  private cachedTools: ReturnType<typeof normalizeTools> | null = null
  private cachedToolsSource: AiChatbotConfig['tools'] | null = null
  // Confirmed entity slots — populated by show_options single-select resolution.
  // Most-recent-first, capped at AI_CHATBOT_CONFIG.confirmedEntities.maxSlots.
  // Per-conversation lifetime: rebuilt with the Conversation instance, never TTL'd.
  private confirmedEntitiesList: ConfirmedEntity[] = []
  private readonly toolPause = new ToolPauseState()
  readonly costs: SessionCosts = createSessionCosts()

  constructor(
    listener: ConversationListener,
    initialMessages: OpenAI.ChatCompletionMessageParam[] = [],
    {
      debug = AI_CHATBOT_CONFIG.debug.defaultLevel,
    }: { debug?: AiChatBotDebugLevel } = {},
  ) {
    this.listener = listener
    this.client = getHostRuntime().bindings.createLlmClient()
    this.messages = initialMessages
    this.log = createLogger(debug)
    this.log.log('Conversation created')
    this.emit([])
  }

  async processUserMessage(
    config: AiChatbotConfig,
    userInput: string,
  ): Promise<void> {
    if (this.isProcessing) {
      this.log.warn(
        'processUserMessage called while already processing — ignored',
      )
      return
    }

    if (this.compactionDisabled) {
      this.messages = [
        ...this.messages,
        {
          role: 'assistant',
          content: AI_CHATBOT_CONFIG.contextWindow.compactionDisabledMessage,
        },
      ]
      this.emit([])
      return
    }

    this.log.group(
      `Turn: "${userInput.slice(0, 60)}${userInput.length > 60 ? '…' : ''}"`,
    )

    const userMessage: OpenAI.ChatCompletionMessageParam = {
      role: 'user',
      content: userInput,
    }

    const prevMessages = this.messages
    this.messages = [...prevMessages, userMessage]
    this.isProcessing = true
    this.abortController = new AbortController()
    this.setThinking(true, 'processUserMessage start')
    const pageCtx = resolvePageContext()
    setPerTurnCallbacks({
      registerToolCallResolver: (id, call, res, rej) => {
        this.toolPause.register(id, call, res, rej)
        this.emit([])
      },
      resolveToolCallResolver: (id, result) =>
        this.toolPause.resolve(id, result),
      pageContext: pageCtx,
    })
    setConfirmedEntityWriter((entity) => this.addConfirmedEntity(entity))
    this.emit([])

    // Wrap tools once per Conversation instance — ExpiryMap state must survive across turns.
    if (this.cachedToolsSource !== config.tools) {
      this.cachedToolsSource = config.tools ?? null
      this.cachedTools = normalizeTools(config.tools ?? []).map((t) =>
        withCache(t),
      )
    }
    const tools = this.cachedTools ?? []
    const toolDefs = tools.length ? tools.map((t) => t.toOpenAI()) : undefined

    const pipeline = config.pipeline
    const gatherModel = pipeline?.gather.model ?? config.model ?? DEFAULT_MODEL
    const synthesizeModel =
      pipeline?.synthesize.model ?? config.model ?? DEFAULT_MODEL
    const compactModel = AI_CHATBOT_CONFIG.models.compact

    const allMessages: OpenAI.ChatCompletionMessageParam[] = [
      ...buildInitialMessages(prevMessages, config.systemPrompt),
      ...maybePageContextMessage(pageCtx),
      ...maybeConfirmedEntitiesMessage(this.confirmedEntitiesList),
      userMessage,
    ]

    this.log.log(
      'gather model:',
      gatherModel,
      '| synthesize model:',
      synthesizeModel,
      '| compact model:',
      compactModel,
      '| tools:',
      tools.map((t) => t.name),
    )
    this.log.verbose('request.messages:', allMessages)
    this.log.verbose('request.tools:', toolDefs)

    // Pre-turn compaction check: compact history if context window is running low
    const {
      totalTokens,
      autoCompactBufferTokens,
      maxConsecutiveCompactFailures,
    } = AI_CHATBOT_CONFIG.contextWindow
    const estimatedTokens = estimateMessagesTokens(allMessages)
    let compactionFailedThisTurn = false
    if (
      estimatedTokens > 0 &&
      estimatedTokens > totalTokens - autoCompactBufferTokens
    ) {
      this.log.log(
        'Context running low (',
        estimatedTokens,
        'est. tokens). Compacting history.',
      )
      try {
        const result = await compactHistory(
          this.client,
          allMessages,
          this.log,
          compactModel,
        )
        if (result.compacted) {
          allMessages.splice(0, allMessages.length, ...result.messages)
          if (result.cost) {
            const recorded = recordTurnCost(this.costs, {
              stage: 'compact',
              model: result.cost.model,
              inputTokens: result.cost.inputCredits,
              outputTokens: result.cost.outputCredits,
              cacheReadInputTokens: 0,
              cacheCreationInputTokens: 0,
              durationMs: result.cost.durationMs,
            })
            this.log.log(
              'Stage compact cost:',
              recorded,
              '| $',
              calcCostUsd(recorded).toFixed(6),
              '| ms:',
              result.cost.durationMs,
            )
          }
        }
        this.consecutiveCompactFailures = 0
      } catch (err) {
        compactionFailedThisTurn = true
        this.consecutiveCompactFailures++
        this.log.warn(
          'Compaction failed:',
          err,
          '| failures:',
          this.consecutiveCompactFailures,
        )
        if (this.consecutiveCompactFailures >= maxConsecutiveCompactFailures) {
          this.compactionDisabled = true
          this.log.warn(
            'Compaction circuit breaker triggered — blocking new turns',
          )
          allMessages.push({
            role: 'assistant',
            content: AI_CHATBOT_CONFIG.contextWindow.compactionDisabledMessage,
          })
          this.storeMessages(allMessages)
          this.isProcessing = false
          this.emit([])
          return
        }
      }
    }

    try {
      // Stage 0: guard + page context resolve in parallel
      const guardModel = pipeline?.guard.model
      this.log.log(
        'Stage 0: guard + resolvePageContext | guardModel:',
        guardModel ?? 'skipped',
      )
      // Cap guard context at 500 chars — large summaries would otherwise dominate the guard token budget.
      const lastAssistantText = getLastAssistantText(allMessages)?.slice(0, 500)

      const [guardResult] = await Promise.all([
        guardModel
          ? runGuard(
              this.client,
              guardModel,
              userInput,
              this.costs,
              this.log,
              lastAssistantText,
              this.abortController?.signal,
            )
          : Promise.resolve({ allow: true, reason: null }),
      ])

      if (!guardResult.allow) {
        this.log.warn('Guard blocked:', guardResult.reason)
        allMessages.push({
          role: 'assistant',
          content: AI_CHATBOT_CONFIG.guard.blockedMessage,
        })
        return
      }

      // Stage 1 (gather) + Stage 2 (synthesize)
      await this.runLoop(
        allMessages,
        tools,
        toolDefs,
        gatherModel,
        synthesizeModel,
        config.temperature,
      )
    } catch (error) {
      this.log.log(
        'Caught error — name:',
        error instanceof Error ? error.name : '(non-Error)',
        '| message:',
        error instanceof Error ? error.message : String(error),
      )
      if (
        error instanceof Error &&
        (error.name === 'AbortError' ||
          error.name === 'APIUserAbortError' ||
          error.message === 'Request was aborted.')
      ) {
        this.log.log('Turn aborted by user')
        // Preserve any partial assistant text already streamed into this.messages
        const partial = this.messages[this.messages.length - 1]
        if (
          partial?.role === 'assistant' &&
          typeof partial.content === 'string' &&
          partial.content.length > 0
        ) {
          allMessages.push({ role: 'assistant', content: partial.content })
        }
      } else {
        this.log.error('Turn error:', error)
        allMessages.push({
          role: 'assistant',
          content: 'An internal error occurred.',
        })
        this.hadError = true
      }
    } finally {
      setPerTurnCallbacks(null)
      setConfirmedEntityWriter(null)
      this.abortController = null
      this.storeMessages(allMessages)
      this.isProcessing = false
      if (
        !this.hadError &&
        !this.compactionDisabled &&
        !compactionFailedThisTurn
      ) {
        this.consecutiveCompactFailures = 0
      }
      this.emit([])
      this.log.log('Turn complete. Messages:', allMessages)
      // estimatedTokens = stripped context size at turn start — what compaction threshold watches
      const sessionTokens = calcSessionActualTokens(this.costs)
      this.log.log(
        'Turn costs:',
        this.costs.turns,
        '| Context (stripped):',
        estimatedTokens,
        'est. tokens',
        '| Session tokens:',
        sessionTokens.input,
        'in /',
        sessionTokens.output,
        'out',
        '| Session $',
        calcSessionCostUsd(this.costs)?.toFixed(6) ?? 'unknown',
      )
      this.log.groupEnd()
    }
  }

  private async runLoop(
    allMessages: OpenAI.ChatCompletionMessageParam[],
    tools: ReturnType<typeof normalizeTools>,
    toolDefs: OpenAI.ChatCompletionTool[] | undefined,
    gatherModel: string,
    synthesizeModel: string,
    temperature: number | undefined,
  ): Promise<void> {
    const toolMap = new Map(tools.map((t) => [t.name, t]))

    // Gather selects tools; only synthesize speaks to the user. A gather pass that
    // returns prose instead of a tool call is therefore not an answer — it is
    // escalated, not shown. Without this the fast model could end a turn on its own
    // unreviewed text, which is how it came to report writes it never performed
    // ("saved as X" with no save_filter call in the turn).
    //
    // Only meaningful when the two stages run different models; if they are the same,
    // re-running would repeat an identical call, so gather's text is already final.
    const canEscalate = gatherModel !== synthesizeModel
    let escalatedToSynthesize = false

    // Per-turn safety guards. Primary is the tool-error count: a model stuck
    // retrying a failing tool errors every round, while a healthy flow — even a
    // data-heavy summary firing many tools — errors ~never. rounds is a cheap
    // backstop against a non-error infinite loop. See AI_CHATBOT_CONFIG.toolLoop.
    let rounds = 0
    let toolErrors = 0

    // Multi-turn tool call loop
    while (true) {
      if (
        toolErrors >= AI_CHATBOT_CONFIG.toolLoop.maxErrors ||
        rounds >= AI_CHATBOT_CONFIG.toolLoop.maxRounds
      ) {
        this.log.log(
          `Tool-call loop stopped — ${toolErrors} errors / ${rounds} rounds ` +
            `(caps: ${AI_CHATBOT_CONFIG.toolLoop.maxErrors} / ${AI_CHATBOT_CONFIG.toolLoop.maxRounds})`,
        )
        allMessages.push({
          role: 'assistant',
          content: AI_CHATBOT_CONFIG.toolLoop.limitReachedMessage,
        })
        this.storeMessages(allMessages)
        this.setThinking(false, 'tool-call loop guard tripped')
        this.emit([])
        break
      }
      rounds++
      // Switch to synthesizeModel once tool results are present in history
      const hasPendingToolResults = allMessages.some((m) => m.role === 'tool')
      const useSynthesize = hasPendingToolResults || escalatedToSynthesize
      const currentModel = useSynthesize ? synthesizeModel : gatherModel
      const currentStage = useSynthesize ? 'synthesize' : 'gather'

      // Each new LLM call starts a new thinking phase — flip back to true so the
      // indicator stays visible across multi-turn tool loops
      this.setThinking(true, `loop start stage=${currentStage}`)
      this.emit([])

      const startMs = Date.now()

      // Mid-stream tracking: concurrent-safe tools dispatched before stream ends
      const inflightConcurrent = new Map<string, Promise<ToolResult>>()
      const dispatchedIds = new Set<string>()

      const stream = this.client.chat.completions.stream(
        {
          model: currentModel,
          messages: allMessages,
          tools: toolDefs,
          temperature,
          stream: true,
          stream_options: { include_usage: true },
        },
        { signal: this.abortController?.signal },
      )

      for await (const _chunk of stream) {
        const snapshot = stream.currentChatCompletionSnapshot?.choices[0]
        if (snapshot?.message) {
          const partial = extractPartialMessage(snapshot.message)
          if (partial) {
            // Suppress preamble text when tool_calls are being streamed
            const hasToolCalls =
              'tool_calls' in partial &&
              Array.isArray(partial.tool_calls) &&
              partial.tool_calls.length > 0
            if (hasToolCalls) {
              // Tool calls are streaming — this is not the final answer; re-enable
              // thinking in case the model emitted preamble text before tool_calls
              this.setThinking(true, 'tool_calls detected in stream')
              const partialAssistant =
                partial as OpenAI.ChatCompletionAssistantMessageParam
              const preamble =
                typeof partialAssistant.content === 'string' &&
                partialAssistant.content.length > 0 &&
                keepsPreamble(partialAssistant.tool_calls)
                  ? partialAssistant.content
                  : null
              const display: OpenAI.ChatCompletionMessageParam = {
                role: 'assistant',
                content: preamble,
                // Cast: partial is narrowed by hasToolCalls guard above but the union type doesn't propagate — structurally safe.
                tool_calls: partialAssistant.tool_calls,
              }
              this.storeMessages([...allMessages, display])
              this.emit([])
            } else if (currentStage === 'gather' && canEscalate) {
              // Gather prose is never user-facing — it will be discarded and the turn
              // re-run on synthesize. Keep the thinking indicator up and stream nothing,
              // so the user never sees text that is about to be replaced.
              this.setThinking(true, 'gather text — pending escalation')
            } else {
              // Text token with no tool_calls — hide thinking as text becomes visible
              this.setThinking(false, 'first text token')
              this.storeMessages([...allMessages, partial])
              this.emit([])
            }
          }

          // Mid-stream dispatch: start concurrent-safe tools as soon as arguments are complete
          const snapshotToolCalls = (
            snapshot.message as PartialAssistantMessage
          ).tool_calls
          for (const tc of snapshotToolCalls ?? []) {
            if (!tc.id || dispatchedIds.has(tc.id)) continue
            const tool = toolMap.get(tc.function?.name ?? '')
            if (!tool || tool.concurrencySafe === false) continue
            if (!isCompleteArguments(tc.function?.arguments)) continue

            dispatchedIds.add(tc.id)
            const fc: FunctionToolCall = {
              id: tc.id,
              type: 'function',
              function: {
                name: tc.function.name,
                arguments: tc.function.arguments,
              },
            }
            inflightConcurrent.set(tc.id, runToolDirect(fc, toolMap, this.log))
          }
        }
      }

      const finalCompletion = await stream.finalChatCompletion()
      const finalMsg = finalCompletion.choices[0]?.message
      const durationMs = Date.now() - startMs

      this.log.verbose('response.completion:', finalCompletion)
      this.log.verbose(`stage.${currentStage}.durationMs:`, durationMs)

      // Record cost. The gateway caches via Bedrock cachePoint blocks injected
      // when the `Prefer: cache-ttl=300` header is present — NOT via Anthropic's
      // native cache_control field. cache_read_input_tokens is reported as credits
      // (~75× smaller than real token count) in prompt_tokens_details.cached_tokens.
      const usage = finalCompletion.usage
      let messageUsage:
        | { inputTokens: number; outputTokens: number; cachedTokens?: number }
        | undefined
      if (usage) {
        // The gateway returns cache-read credits in the standard OpenAI field
        // prompt_tokens_details.cached_tokens, NOT in Anthropic's native
        // cache_read_input_tokens (which is never populated by this gateway).
        const cacheReadCredits = usage.prompt_tokens_details?.cached_tokens ?? 0
        // cache_creation_input_tokens is Anthropic-native; this gateway does not
        // return it — Bedrock cache write cost is factored into the input credits.
        const cacheCreationCredits = 0
        const recorded = recordTurnCost(this.costs, {
          stage: currentStage,
          model: currentModel,
          inputTokens: usage.prompt_tokens ?? 0,
          outputTokens: usage.completion_tokens ?? 0,
          cacheReadInputTokens: cacheReadCredits,
          cacheCreationInputTokens: cacheCreationCredits,
          durationMs,
        })
        messageUsage = {
          inputTokens: creditsToTokens(
            usage.prompt_tokens ?? 0,
            currentModel,
            'input',
          ),
          outputTokens: creditsToTokens(
            usage.completion_tokens ?? 0,
            currentModel,
            'output',
          ),
          ...(cacheReadCredits > 0
            ? {
                cachedTokens: creditsToTokens(
                  cacheReadCredits,
                  currentModel,
                  'input',
                ),
              }
            : {}),
        }
        this.log.log(
          `Stage ${currentStage} cost:`,
          recorded,
          '| $',
          calcCostUsd(recorded).toFixed(6),
          '| ms:',
          durationMs,
          '| tokens:',
          messageUsage.inputTokens,
          'in /',
          messageUsage.outputTokens,
          'out',
        )
      }

      if (!finalMsg) break

      // Add assistant message to history.
      // Preamble before a NARRATION_TOOLS call (e.g. "I found these companies:"
      // above the OptionsCard) stays visible; preamble before any other tool is
      // internal-step narration ("Let me get the write schema first") and is
      // dropped so it never reaches the UI. A turn with no tool calls is the final
      // answer — its content is always kept.
      const hasToolCalls = !!finalMsg.tool_calls?.length
      const assistantMsg: StoredMessage = {
        role: 'assistant',
        content:
          hasToolCalls && !keepsPreamble(finalMsg.tool_calls)
            ? null
            : (finalMsg.content ?? null),
        ...(finalMsg.tool_calls?.length
          ? {
              // Cast: SDK streaming type is `ChatCompletionMessageToolCall[]` but the snapshot type is broader — structurally identical at runtime.
              tool_calls:
                finalMsg.tool_calls as OpenAI.Chat.Completions.ChatCompletionMessageToolCall[],
            }
          : {}),
        ...(messageUsage
          ? {
              usage: {
                model: currentModel,
                inputTokens: messageUsage.inputTokens,
                outputTokens: messageUsage.outputTokens,
                ...(messageUsage.cachedTokens !== undefined
                  ? { cachedTokens: messageUsage.cachedTokens }
                  : {}),
              },
            }
          : {}),
      }

      // Extract function-type tool calls
      const functionCalls = (finalMsg.tool_calls ?? []).filter(
        (tc): tc is FunctionToolCall => tc.type === 'function',
      )

      // Gather produced prose instead of tool calls. Discard it — it was never shown —
      // and re-run the same history on synthesize, which is the only stage allowed to
      // answer. Terminates after one escalation: the next pass runs as synthesize and
      // breaks below if it too returns no tool calls.
      if (!functionCalls.length && currentStage === 'gather' && canEscalate) {
        this.log.log(
          'Gather returned text with no tool calls — discarding and escalating to synthesize',
        )
        escalatedToSynthesize = true
        continue
      }

      allMessages.push(assistantMsg)

      if (!functionCalls.length) break

      this.log.log(
        'Tool calls:',
        functionCalls.map((tc) => tc.function.name),
      )

      // Separate concurrent from exclusive tool calls
      const concurrentCalls = functionCalls.filter(
        (tc) => toolMap.get(tc.function.name)?.concurrencySafe !== false,
      )
      const exclusiveCalls = functionCalls.filter(
        (tc) => toolMap.get(tc.function.name)?.concurrencySafe === false,
      )

      // Active calls drive scroll behaviour in MessageList via useScrollAnchor.
      const activeCalls = functionCalls.map((call) => ({
        id: call.id,
        name: call.function.name,
      }))
      this.storeMessages(allMessages)
      this.emit(activeCalls)

      // Collect concurrent results — pick up any already running from mid-stream dispatch
      const concurrentPromises = concurrentCalls.map(
        (tc) =>
          inflightConcurrent.get(tc.id) ?? runToolDirect(tc, toolMap, this.log),
      )
      const concurrentResults = await Promise.all(concurrentPromises)

      // Run exclusive tools sequentially (after all concurrent work is done)
      const exclusiveResults: ToolResult[] = []
      for (const call of exclusiveCalls) {
        exclusiveResults.push(await runToolDirect(call, toolMap, this.log))
      }

      this.log.log('Tool complete')
      this.emit([])

      // Add tool results in original call order
      const allToolResults = [...concurrentResults, ...exclusiveResults]
      for (const result of functionCalls.map(
        (tc) => allToolResults.find((r) => r.tool_call_id === tc.id)!,
      )) {
        allMessages.push({
          role: 'tool',
          tool_call_id: result.tool_call_id,
          content: result.content,
        })
      }

      // Feed the error guard (checked at loop top). Errors are cumulative across
      // the turn — the model still sees each result and gets a few chances to
      // recover; only sustained failure trips the guard.
      toolErrors += allToolResults.filter((r) =>
        isToolErrorResult(r.content),
      ).length

      this.storeMessages(allMessages)
      this.emit([])
    }
  }

  resolveToolCall(toolCallId: string, result: unknown): void {
    this.toolPause.resolve(toolCallId, result)
    this.emit([])
  }

  abort(): void {
    this.abortController?.abort()
  }

  // Confirmed-entity slot API, consumed by resolveEntityReference. Populated by
  // show_options single-select resolution; drives the prompt block + reset semantics.
  get confirmedEntities(): readonly ConfirmedEntity[] {
    return this.confirmedEntitiesList
  }

  addConfirmedEntity(entity: ConfirmedEntity): void {
    // Move-to-front on duplicate id, otherwise prepend. FIFO cap at AI_CHATBOT_CONFIG.confirmedEntities.maxSlots.
    const existing = this.confirmedEntitiesList.findIndex(
      (e) => e.id === entity.id && e.dataClass === entity.dataClass,
    )
    if (existing >= 0) {
      this.confirmedEntitiesList.splice(existing, 1)
    }
    this.confirmedEntitiesList.unshift(entity)
    if (
      this.confirmedEntitiesList.length >
      AI_CHATBOT_CONFIG.confirmedEntities.maxSlots
    ) {
      this.confirmedEntitiesList.length =
        AI_CHATBOT_CONFIG.confirmedEntities.maxSlots
    }
  }

  private setThinking(value: boolean, _reason: string): void {
    // isThinking transitions are no longer logged at the default debug level —
    // the behaviour is stable. Re-enable as `this.log.log(...)` if a regression
    // reappears.
    this.isThinking = value
  }

  // Strip per-turn injections before storing — they are always rebuilt fresh from
  // live page/entity state each turn. Keeping them in this.messages would leak them
  // to the UI (they are user-role messages and the chat renders all user messages).
  private storeMessages(messages: OpenAI.ChatCompletionMessageParam[]): void {
    this.messages = messages.filter((m) => !isPerTurnInjection(m))
  }

  private emit(activeToolCalls: ConversationState['activeToolCalls']): void {
    const pendingToolCalls = this.toolPause.getPending()
    this.listener({
      messages: this.messages,
      isProcessing: this.isProcessing,
      isThinking:
        this.isThinking && this.isProcessing && pendingToolCalls.length === 0,
      activeToolCalls,
      pendingToolCalls,
      hasError: this.hadError || undefined,
      compactionDisabled: this.compactionDisabled || undefined,
    })
  }
}
